import { BarChart3, FileSpreadsheet, Image as ImageIcon, Shapes, Table2, type LucideIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { BadgeProps } from "@/components/ui/badge";

export type ArtifactTypeKey = "Sheet" | "Table" | "Chart" | "Image" | "Object";

interface TypeMeta {
  icon: LucideIcon;
  variant: BadgeProps["variant"];
  /** How the artifact behaves once it reaches the report. Table keeps its data;
   *  Chart and Image are both inserted as pictures, which is why they share a
   *  colour — the label tells you what it is, the colour what it becomes. */
  behaviour: string;
}

export const ARTIFACT_TYPES: Record<ArtifactTypeKey, TypeMeta> = {
  Table: { icon: Table2, variant: "brand", behaviour: "Stays editable in the report" },
  Chart: { icon: BarChart3, variant: "warning", behaviour: "Inserted as an image" },
  Image: { icon: ImageIcon, variant: "warning", behaviour: "Inserted as an image" },
  Sheet: { icon: FileSpreadsheet, variant: "secondary", behaviour: "A whole sheet from the model" },
  // A grouped drawing. The platform cannot read one directly, so it only
  // reaches a report if the modeller exports it as a picture.
  Object: { icon: Shapes, variant: "secondary", behaviour: "Needs exporting as a picture" },
};

/** The API never constrains `type`, and rows persisted before the Appendix
 *  register's vocabulary landed hold lowercase "table"/"chart". Match
 *  case-insensitively; anything unrecognised returns null so the caller can
 *  show it verbatim rather than dropping it. */
export const normalizeArtifactType = (raw: string | null | undefined): ArtifactTypeKey | null => {
  const match = (Object.keys(ARTIFACT_TYPES) as ArtifactTypeKey[]).find(
    (key) => key.toLowerCase() === (raw ?? "").trim().toLowerCase()
  );
  return match ?? null;
};

interface ArtifactTypeBadgeProps {
  type: string | null | undefined;
  className?: string;
}

const ArtifactTypeBadge = ({ type, className }: ArtifactTypeBadgeProps) => {
  const key = normalizeArtifactType(type);

  if (!key) {
    return (
      <Badge variant="secondary" appearance="outlined" className={className}>
        {type || "Unknown"}
      </Badge>
    );
  }

  const { icon: Icon, variant, behaviour } = ARTIFACT_TYPES[key];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={variant} appearance="outlined" className={`gap-1 ${className ?? ""}`}>
          <Icon className="w-3 h-3" />
          {key}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{behaviour}</TooltipContent>
    </Tooltip>
  );
};

export default ArtifactTypeBadge;
